import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { VscFolder, VscMarkdown } from 'react-icons/vsc';
import { FiGithub, FiExternalLink } from 'react-icons/fi';

// --- Projects Data ---
const projectsData = [
  {
    id: 1,
    title: "aiden-ai",
    description: "Aiden is an advanced AI-powered chat interface that combines the power of artificial intelligence with natural voice interaction capabilities. It represents the next generation of human-computer interaction, offering both text and voice-based communication options.",
    techStack: ["React", "Tailwind CSS", "Framer Motion", "Node.js", "WebSocket", "MongoDB"],
    githubLink: "https://github.com/YadamDheerajReddy/Aiden_AI",
    liveLink: "https://aiden-ai-dusky.vercel.app/",
  },
  {
    id: 2,
    title: "sumuri",
    description: "Sumuri provides AI-driven text translation and summarization services in one streamlined platform, enabling users to effortlessly break language barriers and distill complex information into clear, concise insights.",
    techStack: ["React", "Tailwind CSS", "Express", "Node.js", "Firebase", "REST APIs"],
    githubLink: "https://github.com/YadamDheerajReddy/sumuri",
    liveLink: "https://sumuri.vercel.app/",
  },
  {
    id: 3,
    title: "skill-sprout",
    description: "Skill Sprout is an AI-powered skill tree generator designed to help individuals, educators, and organizations map out learning pathways with precision and clarity. By analyzing user goals, current abilities, and industry trends, Skill Sprout automatically creates dynamic, visually engaging skill trees that break complex subjects into manageable milestones.",
    techStack: ["React", "Tailwind CSS", "Framer Motion", "Firebase",  "Express", "Node.js", "REST APIs"],
    githubLink: "https://github.com/YadamDheerajReddy/skillsprout-dev",
    liveLink: "https://skillsprout-two.vercel.app/",
  },
  {
    id: 4,
    title: "tonys-angel-tattooz",
    description: "A complete custom website built for Tony's Angel Tattooz Studio, a tattoo studio looking to bring its portfolio and bookings online. The site showcases the studio's tattoo work and gives visitors a clear path to get in touch and book a session.",
    techStack: ["React", "Tailwind CSS", "Framer Motion"],
    liveLink: "https://www.tonysangeltattooz.in/",
  },
  {
    id: 5,
    title: "kinetx-labs",
    description: "A custom landing page built for Kinetx Labs to give the business a polished online presence and communicate what they do to visitors clearly and quickly.",
    techStack: ["React", "Tailwind CSS", "Framer Motion"],
    liveLink: "https://www.kinetxlabs.com/",
  },
  {
    id: 6,
    title: "examguard",
    description: "A landing page built for ExamGuard, a SaaS product, designed to explain what the product does and guide visitors toward signing up.",
    techStack: ["React", "Tailwind CSS", "Framer Motion"],
    liveLink: "https://examguard.online",
  },
  {
    id: 7,
    title: "echo",
    description: "ECHO (Everyday Computing Human Operator) is an AI-native command palette for Windows, built offline-first by design: the AI runs on your own machine via Ollama, speech-to-text runs on your own CPU via Whisper.cpp, OCR uses Windows' own built-in engine, and weather/web search use free, keyless APIs. Nothing requires an account, a subscription, or your data leaving the machine. Cloud AI (your own OpenAI key) is available if you want it, strictly opt-in, and never a hard dependency.",
    techStack: ["Tauri V2", "React", "RUST", "Ollama"],
    githubLink: "https://github.com/YadamDheerajReddy/ECHO-Everyday_Computing_Human_Operator",
  },
];

const Projects = () => {
  // State to hold the currently selected project object
  const [selectedProject, setSelectedProject] = useState(projectsData[0]);

  return (
    <section id="projects" className="bg-black grainy py-24 sm:py-32 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.h2
          className="text-4xl md:text-5xl font-bold text-text-primary text-center mb-12 font-heading"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          My Work
        </motion.h2>

        {/* IDE Container */}
        <div className="w-full h-[70vh] max-h-[700px] min-h-[600px] bg-[#1e1e1e] rounded-xl shadow-2xl border border-white/10 flex font-mono">
          
          {/* Left Panel: File Explorer */}
          <div className="w-1/4 md:w-1/5 border-r border-white/10 p-4 overflow-y-auto">
            <h3 className="text-sm text-text-secondary uppercase tracking-widest mb-4">Explorer</h3>
            <ul>
              {projectsData.map((project) => (
                <li
                  key={project.id}
                  onClick={() => setSelectedProject(project)}
                  className={`flex items-center gap-2 p-2 rounded-md cursor-pointer transition-colors ${
                    selectedProject.id === project.id ? 'bg-accent/10 text-text-primary' : 'text-text-secondary hover:bg-white/5'
                  }`}
                >
                  <VscFolder className={selectedProject.id === project.id ? 'text-accent' : ''} />
                  <span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis">{project.title}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Panel: Editor */}
          <div className="w-3/4 md:w-4/5 flex flex-col">
            {/* Tab Bar */}
            <div className="flex-shrink-0 bg-primary border-b border-white/10">
              <div className="inline-flex items-center gap-2 p-3 bg-[#1e1e1e] border-r border-white/10">
                <VscMarkdown className="text-accent" />
                <span className="text-sm text-text-primary">README.md</span>
              </div>
            </div>
            
            {/* Content Area */}
            <div className="p-6 md:p-8 overflow-y-auto flex-grow">
              <AnimatePresence mode="wait">
                <motion.div
                  key={selectedProject.id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.25 }}
                  className="space-y-6"
                >
                  <h1 className="text-3xl font-bold text-text-primary font-heading"># {selectedProject.title}</h1>
                  <div className="flex items-center gap-4 text-text-secondary text-xl">
                    {selectedProject.githubLink && <a href={selectedProject.githubLink} target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors flex items-center gap-1"><FiGithub /> <span className="text-sm">GitHub</span></a>}
                    {selectedProject.liveLink && <a href={selectedProject.liveLink} target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors flex items-center gap-1"><FiExternalLink /> <span className="text-sm">Live Demo</span></a>}
                  </div>
                  {/* <img src={selectedProject.imageUrl} alt={selectedProject.title} className="rounded-lg border border-white/10 mt-4" /> */}
                  <p className="text-text-secondary leading-relaxed">{selectedProject.description}</p>
                  <div>
                    <h4 className="font-bold text-text-primary mb-2">Technologies Used:</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.techStack.map((tech) => (
                        <span key={tech} className="text-xs text-accent bg-accent/10 px-2 py-1 rounded-full">{tech}</span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Projects;